import { FeatureName } from 'v5/platform/feature-management/feature-management';
import { Permission } from '../PermissionService/PermissionService';
import { AtellioFeatureGroupDefinition } from 'v5/features';
import { RemoteData } from '@passionware/monads';

export const featureFlaggedPemissions = {
  productions: [
    Permission.PRODUCTION_CREATE,
    Permission.PRODUCTION_UPDATE,
    Permission.PRODUCTION_ARCHIVE
  ],
  shortlists: [
    Permission.SHORTLIST_CREATE,
    Permission.SHORTLIST_UPDATE,
    Permission.SHORTLIST_ARCHIVE,
    Permission.SHORTLIST_PUBLISH
  ],
  callouts: [
    Permission.CALLOUT_CREATE,
    Permission.CALLOUT_UPDATE,
    Permission.CALLOUT_ARCHIVE,
    Permission.CALLOUT_PUBLISH
  ],
  callsheets: [
    Permission.CALLSHEET_CREATE,
    Permission.CALLSHEET_UPDATE,
    Permission.CALLSHEET_ARCHIVE,
    Permission.CALLSHEET_SEND
  ],
  bookings: [
    Permission.BOOKING_CREATE,
    Permission.BOOKING_UPDATE,
    Permission.BOOKING_ARCHIVE,
    Permission.BOOKING_SEND_CONFIRMATION
  ],
  resources: [
    Permission.RESOURCE_CREATE,
    Permission.RESOURCE_UPDATE,
    Permission.RESOURCE_ARCHIVE
  ]
} satisfies Partial<
  Record<FeatureName<AtellioFeatureGroupDefinition>, Array<Permission>>
>;

// Pre-computed reverse mapping for O(1) lookup
export const actionToFeatureMap = new Map<
  Permission,
  FeatureName<AtellioFeatureGroupDefinition>
>();

// Build the reverse mapping once at module load time
Object.entries(featureFlaggedPemissions).forEach(([feature, permissions]) => {
  permissions.forEach(permission => {
    actionToFeatureMap.set(
      permission,
      feature as FeatureName<AtellioFeatureGroupDefinition>
    );
  });
});

type FeaturesWithPermissions = keyof typeof featureFlaggedPemissions;

type FeaturesWithoutPermissions = Exclude<
  FeatureName<AtellioFeatureGroupDefinition>,
  FeaturesWithPermissions
>;

export const specialCapabilities = ['manage_feature_flags'] as const;

export type SpecialCapability = (typeof specialCapabilities)[number];

export type FeatureQuery = {
  feature: FeaturesWithoutPermissions;
};
export type ActionQuery = { action: Permission | SpecialCapability };

export type AccessQuery =
  /**
   * AccessQuery:
   * - Use `{ feature }` when you want to check access for a feature that does not require a specific permission.
   *   These are features that are only controlled by feature flags and do not have associated permissions.
   *   Example: `{ feature: 'booking-statistics' }`
   *
   * - Use `{ action }` when you want to check access for a permission that is coupled to a feature flag.
   *   In this case, the system will check both the relevant feature flag and the required permission behind the scenes.
   *   Example: `{ action: Permission.PRODUCTION_CREATE }`
   *
   * The system automatically determines which feature flag (if any) is associated with the given action and checks both
   * the flag and the permission for access.
   */
  FeatureQuery | ActionQuery;
export interface AccessService {
  /**
   * Hook returning a reactive function to check if user has access
   * Combines feature flag and permission logic
   */
  useCan: () => (access: AccessQuery) => RemoteData<boolean>;
  /**
   * Returns whether user has access at call time
   * Combines feature flag and permission logic
   */
  can: (access: AccessQuery) => Promise<boolean>;
}

export interface WithAccessService {
  accessService: AccessService;
}
